import React, { useContext, Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { AuthContext, AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import Nav from './components/Nav';
import Landing from './pages/Landing';
import Auth from './pages/Auth';
import Dashboard from './pages/Dashboard';
import ClientPortal from './pages/ClientPortal';
import Contact from './pages/Contact';
import SiteEditor from './pages/SiteEditor';
import Onboarding from './pages/Onboarding';
import SITE_REGISTRY from './SiteRegistry';
import './App.css';

// ── DÉTECTION MULTI-SITES ─────────────────────────────────────────────────────
// En production : REACT_APP_SITE est injecté au moment du build
// En dev        : préfixer l'URL avec /nomsite pour prévisualiser
const ADMIN_ROUTES = ['auth', 'dashboard', 'contact', 'signup', 'editor', ''];

function detectSite() {
  if (process.env.REACT_APP_SITE && process.env.REACT_APP_SITE !== 'admin') {
    return process.env.REACT_APP_SITE;
  }
  const seg = window.location.pathname.split('/')[1] || '';
  if (seg && !ADMIN_ROUTES.includes(seg)) return seg;
  return null;
}

// ── CHARGEMENT DES SITES ─────────────────────────────────────────────────────
const SiteSpinner = () => (
  <div style={{ background:'#080808', minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center' }}>
    <div style={{ width:32, height:32, border:'2px solid rgba(255,255,255,0.08)', borderTop:'2px solid rgba(255,255,255,0.5)', borderRadius:'50%', animation:'spin 0.7s linear infinite' }}/>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

function SiteLoader({ siteId }) {
  const SiteApp = SITE_REGISTRY[siteId];
  if (!SiteApp) return (
    <div style={{ background:'#080808', minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:12 }}>
      <div style={{ fontSize:'2rem' }}>🔧</div>
      <h2 style={{ color:'#EAEAEA', fontWeight:800 }}>Site "{siteId}" en construction</h2>
      <p style={{ color:'#555', fontSize:'0.88rem' }}>Ajouter l'import dans <code style={{color:'#C8FF00'}}>client/src/SiteRegistry.js</code></p>
    </div>
  );
  return (
    <Suspense fallback={<SiteSpinner />}>
      <SiteApp />
    </Suspense>
  );
}

// ── PORTAL ONLY MODE (build de production sans dashboard admin) ───────────────
const PORTAL_ONLY = process.env.REACT_APP_PORTAL_ONLY === 'true';

// ── ROUTER DASHBOARD ─────────────────────────────────────────────────────────
function DashboardRouter() {
  const { currentUser, userRole, loading } = useContext(AuthContext);

  if (loading) return (
    <div style={{ minHeight:'100vh', background:'#060608', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:12 }}>
      <div style={{ width:32, height:32, border:'2px solid rgba(200,255,0,0.15)', borderTop:'2px solid #C8FF00', borderRadius:'50%', animation:'spin 0.7s linear infinite' }}/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
  if (!currentUser) return <Navigate to="/auth" replace />;
  if (PORTAL_ONLY || userRole !== 'admin') return <ClientPortal />;
  return <Dashboard />;
}

function EditorRouter() {
  const { currentUser, userRole, loading } = useContext(AuthContext);
  if (loading) return null;
  if (!currentUser) return <Navigate to="/auth" replace />;
  if (!PORTAL_ONLY && userRole === 'admin') return <SiteEditor />;
  return <Navigate to="/dashboard" replace />;
}

function NavWrapper() {
  const loc = useLocation();
  if (loc.pathname.startsWith('/dashboard')) return null;
  if (loc.pathname.startsWith('/editor')) return null;
  if (loc.pathname.startsWith('/signup')) return null;
  return <Nav />;
}

// ── APP PRINCIPALE ────────────────────────────────────────────────────────────
function MainApp() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <NavWrapper />
          <Routes>
            <Route path="/"          element={<Landing />} />
            <Route path="/auth"      element={<Auth />} />
            <Route path="/signup"    element={<Onboarding />} />
            <Route path="/dashboard" element={<DashboardRouter />} />
            <Route path="/editor/:identifier" element={<EditorRouter />} />
            <Route path="/contact"   element={<Contact />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

// ── ENTRY POINT ──────────────────────────────────────────────────────────────
export default function App() {
  const site = detectSite();
  if (site) return <SiteLoader siteId={site} />;
  return <MainApp />;
}
